import { TextareaHTMLAttributes, useLayoutEffect, useRef } from 'react'

type Props = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  value: string
  minRows?: number
  maxRows?: number
}

/** A textarea that grows with its content from `minRows` up to `maxRows`, then scrolls — so short replies
 *  stay compact and long, multi-line instructions get room without the user dragging a resize handle. */
export function AutoGrowTextarea({ value, minRows = 2, maxRows = 8, style, ...rest }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'                                   // reset so scrollHeight reflects current content
    const cs = getComputedStyle(el)
    const line = parseFloat(cs.lineHeight) || 20
    const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0)
    const max = line * maxRows + pad
    const next = Math.min(el.scrollHeight, max)
    if (next > 0) el.style.height = `${next}px`                // jsdom reports scrollHeight 0 — leave height to CSS there
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden'
  }, [value, maxRows])

  return <textarea ref={ref} rows={minRows} value={value} style={{ resize: 'none', ...style }} {...rest} />
}
